// src/components/User/Components/UserDataGrid.js
import React, { useEffect, useRef, useCallback, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useDispatch, useSelector } from "react-redux";
import { getUserColumns } from "./UserDataGridColumns";
import UserCustomToolbar from "./UserCustomToolbar";
import { fetchUsers } from "../../../../store/Users/usersThunks";

function UserDataGrid({ onEditUser, onAddUser, apiRef }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { users, loading, currentPage, hasMore } = useSelector(
    (state) => state.users
  );
  const containerRef = useRef(null);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Initial load: get the first page.
  useEffect(() => {
    dispatch(fetchUsers({ page: 1 }));
  }, [dispatch]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    dispatch(fetchUsers({ page: 1, search: query }));
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    const validFilters = filterModel.items.filter(
      (filter) => filter.value !== undefined && filter.value !== ""
    );
    dispatch(
      fetchUsers({
        page: 1,
        filters: validFilters,
        sortModel: newSortModel,
        search: searchQuery,
      })
    );
  };

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    const validFilters = newFilterModel.items.filter(
      (filter) => filter.value !== undefined && filter.value !== ""
    );
    if (validFilters.length > 0) {
      dispatch(
        fetchUsers({
          page: 1,
          filters: validFilters,
          sortModel: [],
          search: searchQuery,
        })
      );
    }
  };

  const handleRefresh = () => {
    dispatch(fetchUsers({ page: 1, search: searchQuery }));
  };

  const handleScroll = useCallback(() => {
    if (loading || !hasMore) return;
    const container = containerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      dispatch(fetchUsers({ 
        page: currentPage + 1,
        search: searchQuery,
      }));
    }
  }, [dispatch, loading, hasMore, currentPage, searchQuery]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <Box sx={{ height: "100%" }}>
      <DataGridPro
        apiRef={apiRef}
        checkboxSelection
        disableRowSelectionOnClick
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(newSelection) =>
          setSelectionModel(newSelection)
        }
        rows={users}
        columns={getUserColumns(onEditUser)}
        disableExtendRowFullWidth={false}
        sortingMode="server"
        filterMode="server"
        filterDebounceMs={500}
        onSortModelChange={handleSortModelChange}
        loading={loading}
        autoHeight={false}
        hideFooter
        initialState={{
          columns: {
            columnVisibilityModel: {
              // Customize as needed – for example, hide certain columns
            },
          },
        }}
        slots={{
          toolbar: null,
        }}
        slotProps={{
          filterPanel: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              '& .MuiButton-root': {
                backgroundColor: (theme) => theme.palette.background.button,
                color: (theme) => theme.palette.common.white,
                textTransform: "none",
                px: 2,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.background.button,
                },
              },
              '& .MuiIconButton-root': {
                color: theme.palette.text.primary,
              },
            }
          },
          columnsPanel: {
            sx: {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          },
        }}
        sx={{
          width: '100%',
          height: '100%',
          "& .MuiDataGrid-detailPanelToggleCell": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
            display: "flex !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: (theme) => theme.palette.background.paper,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            position: "sticky",
            top: 0,
            zIndex: 1,
          },
          "& .MuiDataGrid-cell": {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            whiteSpace: 'normal',
            wordWrap: 'break-word',
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
          "& .MuiDataGrid-main": {
            width: '100%',
          },
        }}
      />
    </Box>
  );
}

export default UserDataGrid;
